import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { ArrowRight, Clock, Shield, CheckCircle } from 'lucide-react';

const targetCurrencies = ['USD', 'EUR', 'GBP'];
const currencyNames = {
  'USD': 'US Dollar',
  'EUR': 'Euro', 
  'GBP': 'British Pound'
};

export default function HeroSection() {
  const [previewRates, setPreviewRates] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const fetchPreviewRates = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('https://api.nbp.pl/api/exchangerates/tables/C?format=json');
        
        if (response.ok) {
          const data = await response.json();
          const ratesTable = data[0];
          
          const filteredRates = ratesTable.rates
            .filter(rate => targetCurrencies.includes(rate.code))
            .map(rate => ({
              ...rate,
              // Calculate Buy/Sell with 0.025 PLN margin
              buyRate: rate.bid - 0.025,
              sellRate: rate.ask + 0.025
            }));
          
          setPreviewRates(filteredRates);
          setLastUpdated(ratesTable.effectiveDate);
        }
      } catch (error) {
        console.error('Error fetching preview rates:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreviewRates();
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 pt-16">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M30 30l15-15v30l-15-15zm-15 0l-15 15v-30l15 15z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left select-text">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Professional Currency Exchange for{' '}
              <span className="text-green-400">Business Clients</span>
            </h1>
            
            <p className="text-xl text-blue-100 mb-2 leading-relaxed">
              Secure transactions, competitive rates, professional service for businesses.
            </p>
            
            <p className="text-lg text-blue-200 mb-8">
              Reliable service • Best exchange rates • Professional support
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6 relative z-20">
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg font-semibold group shadow-xl cursor-pointer"
                type="button"
              >
                Contact
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <Button
                onClick={() => scrollToSection('rates')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 text-lg font-semibold shadow-xl cursor-pointer"
                type="button"
              >
                Check Rates
              </Button>
            </div>

            <div className="flex items-center justify-center lg:justify-start space-x-4 text-blue-200 text-sm">
              <div className="flex items-center space-x-1">
                <Shield className="w-4 h-4" />
                <span>Secure</span>
              </div>
              <div className="flex items-center space-x-1">
                <CheckCircle className="w-4 h-4" />
                <span>Professional</span>
              </div>
            </div>
          </div>

          {/* Right Content - Enhanced Rates Preview */}
          <div className="relative">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-white font-semibold text-lg">Live Exchange Rates</h3>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-400 text-sm font-medium">Live</span>
                </div>
              </div>
              
              <div className="space-y-4">
                {isLoading ? (
                  // Loading skeleton
                  Array(3).fill(0).map((_, index) => (
                    <div key={index} className="animate-pulse">
                      <div className="flex justify-between items-center py-3 border-b border-white/10">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-6 bg-white/20 rounded"></div>
                          <div className="w-20 h-4 bg-white/20 rounded"></div>
                        </div>
                        <div className="text-right space-y-1">
                          <div className="w-16 h-4 bg-white/20 rounded"></div>
                          <div className="w-16 h-4 bg-white/20 rounded"></div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  previewRates.map((rate) => (
                    <div key={rate.code} className="flex justify-between items-center py-3 border-b border-white/10 last:border-b-0">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-8 bg-green-600 rounded-md flex items-center justify-center">
                          <span className="text-white text-sm font-bold">{rate.code}</span>
                        </div>
                        <div>
                          <div className="text-white font-medium">{currencyNames[rate.code]}</div>
                          <div className="text-blue-200 text-xs">1 {rate.code} = PLN</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center space-x-4">
                          <div>
                            <div className="text-xs text-blue-300 mb-1">Buy</div>
                            <div className="text-green-400 font-bold text-lg">
                              {rate.buyRate.toFixed(4)}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-blue-300 mb-1">Sell</div>
                            <div className="text-red-400 font-bold text-lg">
                              {rate.sellRate.toFixed(4)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
                <div className="flex items-center space-x-2 text-blue-200 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>Last updated: {lastUpdated}</span>
                </div>
                <button
                  onClick={() => scrollToSection('rates')}
                  className="text-green-400 hover:text-green-300 text-sm font-medium transition-colors"
                >
                  View all rates →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}